import { useCallback, useEffect, useRef, useState } from "react";

import { invocarFuncao } from "@/lib/edgeFunction";
import { paraBase64, paraWav } from "./audioWav";

/**
 * Microfone da North IA e da caixinha do playbook: o vendedor aperta, fala,
 * aperta o quadrado, e o que ele disse fica no campo. O quadrado só PARA de
 * ouvir; enviar é com Enter ou Enviar, depois de conferir (pedido do Rick).
 *
 * O navegador só GRAVA (MediaRecorder, que Chrome, Edge, Brave, Firefox e
 * Safari têm). Quem transcreve é a edge function `transcrever-voz`, com o
 * Gemini. A primeira versão usava o ditado do navegador (Web Speech API), que
 * no Brave, no Chromium e em Chrome/Edge de Linux responde "network" sempre:
 * a API existe, o serviço de voz do Google por trás, não.
 *
 * O texto vai aparecendo enquanto se fala: a cada ~3 s o áudio gravado ATÉ
 * ALI é transcrito de novo e substitui a prévia (2–3 s atrás da fala). Ao
 * parar, uma transcrição do áudio inteiro dá o texto final do campo.
 *
 * A chave do Gemini é uma só (DEV e PROD, North IA inclusive) e, em 29/09,
 * estava no plano gratuito: prévia que volta "limite de uso" suspende as
 * prévias daquela gravação — a final ainda tenta.
 *
 * Só grava enquanto o botão está ligado, e é a voz do VENDEDOR. Ouvir a
 * ligação com o cliente é outra conversa (consentimento, LGPD) e não é isto.
 */

/**
 * De quantos pedaços de 1 s sai uma prévia — e só se a anterior já voltou,
 * então nunca há duas no ar. 60 s de fala custam umas 15 chamadas curtas.
 */
export const PEDACOS_POR_PREVIA = 2;

/** A frase do 429 da transcrever-voz (MSG_LIMITE). */
const LIMITE_DE_USO = /limite de uso/i;

/** Teto de uma gravação: 60 s de WAV 16 kHz ≈ 2 MB, dentro do limite da função. */
export const LIMITE_GRAVACAO_S = 60;

export const gravacaoSuportada = (): boolean =>
  typeof navigator !== "undefined" &&
  typeof navigator.mediaDevices?.getUserMedia === "function" &&
  typeof MediaRecorder !== "undefined";

/** O que dizer quando o navegador não entrega o microfone (nome do DOMException). */
export const mensagemDoErro = (nome: string): string => {
  if (nome === "NotAllowedError" || nome === "SecurityError") {
    return "Microfone bloqueado. A permissão fica no cadeado ao lado do endereço do site.";
  }
  if (nome === "NotFoundError" || nome === "OverconstrainedError")
    return "Nenhum microfone encontrado neste computador.";
  if (nome === "NotReadableError")
    return "Microfone em uso por outro programa.";
  return "Não foi possível ligar o microfone.";
};

/** Grava → WAV → transcrição no servidor. */
export async function transcreverNoServidor(gravacao: Blob): Promise<string> {
  const audio = await paraBase64(await paraWav(gravacao));
  const { dados, erro } = await invocarFuncao<{ texto?: string }>(
    "transcrever-voz",
    {
      body: { audio, tipo: "audio/wav" },
      quando: "não foi possível transcrever o áudio",
    },
  );
  if (erro) throw new Error(erro);
  return (dados?.texto ?? "").trim();
}

const juntar = (antes: string, falado: string) =>
  [antes.trim(), falado].filter(Boolean).join(" ");

/** O que fazer com o áudio ao parar: transcrever para o campo, ou jogar fora. */
type AoParar = "manter" | "descartar";

export interface OpcoesDitado {
  /** O campo inteiro depois da transcrição (o que já estava + o falado). */
  aoMudarTexto: (texto: string) => void;
  transcrever?: (gravacao: Blob) => Promise<string>;
  suportado?: boolean;
}

export function useDitado({
  aoMudarTexto,
  transcrever = transcreverNoServidor,
  suportado = gravacaoSuportada(),
}: OpcoesDitado) {
  const [ouvindo, setOuvindo] = useState(false);
  const [transcrevendo, setTranscrevendo] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const gravador = useRef<MediaRecorder | null>(null);
  const aoParar = useRef<AoParar>("manter");
  const montado = useRef(true);
  const limite = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** Cada gravação tem um número; transcrição de outro número chega tarde e é ignorada. */
  const sessao = useRef(0);
  // As funções mudam a cada render da tela; a gravação usa sempre a última.
  const callbacks = useRef({ aoMudarTexto, transcrever });
  callbacks.current = { aoMudarTexto, transcrever };

  const encerrar = useCallback((destino: AoParar) => {
    aoParar.current = destino;
    if (gravador.current?.state === "recording") gravador.current.stop();
  }, []);
  /** O quadrado: para de ouvir; o texto final vai para o campo, sem enviar. */
  const parar = useCallback(() => encerrar("manter"), [encerrar]);
  /**
   * Enviou (Enter) com o microfone ligado ou com a transcrição final ainda
   * no ar: desliga, joga o áudio fora e ignora a final — vai o que está no
   * campo (a prévia). Sem isso, parar custava esperar a final (~2 s).
   */
  const descartar = useCallback(() => {
    sessao.current++;
    setTranscrevendo(false);
    encerrar("descartar");
  }, [encerrar]);

  const iniciar = useCallback(
    async (textoAtual = "") => {
      if (!suportado || gravador.current) return;
      setErro(null);
      let stream: MediaStream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (e) {
        setErro(mensagemDoErro((e as DOMException)?.name ?? ""));
        return;
      }
      if (!montado.current) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }
      const minha = ++sessao.current;
      const valendo = () => montado.current && sessao.current === minha;
      const rec = new MediaRecorder(stream);
      const pedacos: Blob[] = [];
      const tipo = () => rec.mimeType || "audio/webm";
      aoParar.current = "manter";

      // Prévia: o áudio até aqui (os pedaços juntos são um arquivo válido,
      // o cabeçalho vem no primeiro). Melhor esforço: se falhar, a final avisa.
      let previaNoAr = false;
      let previasSuspensas = false;
      let desdeAPrevia = 0;
      const previa = async () => {
        previaNoAr = true;
        desdeAPrevia = 0;
        try {
          const falado = await callbacks.current.transcrever(
            new Blob(pedacos, { type: tipo() }),
          );
          // Depois de parar, quem manda é a transcrição final.
          if (valendo() && rec.state === "recording" && falado) {
            callbacks.current.aoMudarTexto(juntar(textoAtual, falado));
          }
        } catch (e) {
          // Prévia perdida: a próxima (ou a final) cobre. Mas no limite de uso,
          // insistir só gasta a cota que a final e a North IA vão precisar.
          if (LIMITE_DE_USO.test(e instanceof Error ? e.message : ""))
            previasSuspensas = true;
        } finally {
          previaNoAr = false;
        }
      };
      rec.ondataavailable = (e) => {
        if (e.data.size > 0) pedacos.push(e.data);
        desdeAPrevia++;
        if (
          rec.state === "recording" &&
          !previaNoAr &&
          !previasSuspensas &&
          desdeAPrevia >= PEDACOS_POR_PREVIA
        )
          void previa();
      };
      rec.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop()); // apaga a luz do microfone
        if (limite.current) clearTimeout(limite.current);
        gravador.current = null;
        if (!montado.current) return;
        setOuvindo(false);
        const destino = aoParar.current;
        if (!valendo()) return;
        const gravacao = new Blob(pedacos, { type: tipo() });
        if (destino === "descartar" || gravacao.size === 0) return;
        setTranscrevendo(true);
        try {
          const falado = await callbacks.current.transcrever(gravacao);
          if (!valendo()) return;
          if (!falado) {
            setErro(
              "Não foi possível reconhecer o áudio. Tente novamente mais perto do microfone.",
            );
            return;
          }
          callbacks.current.aoMudarTexto(juntar(textoAtual, falado));
        } catch (e) {
          if (valendo())
            setErro(
              e instanceof Error
                ? e.message
                : "Não foi possível transcrever o áudio.",
            );
        } finally {
          if (valendo()) setTranscrevendo(false);
        }
      };
      rec.start(1000); // um pedaço por segundo, para as prévias
      gravador.current = rec;
      setOuvindo(true);
      // No teto, para como se fosse o quadrado.
      limite.current = setTimeout(() => {
        aoParar.current = "manter";
        if (rec.state === "recording") rec.stop();
      }, LIMITE_GRAVACAO_S * 1000);
    },
    [suportado],
  );

  // Sair da tela gravando: descarta e desliga o microfone.
  useEffect(() => {
    montado.current = true;
    return () => {
      montado.current = false;
      aoParar.current = "descartar";
      if (gravador.current?.state === "recording") gravador.current.stop();
    };
  }, []);

  return { suportado, ouvindo, transcrevendo, erro, iniciar, parar, descartar };
}
